import React, { useEffect, useId, useRef } from 'react';
import { Icon } from '../icons';

/**
 * Checkbox — styled checkbox built on a native input for accessibility.
 *
 * Props:
 *   checked, onChange, disabled: native semantics
 *   indeterminate: boolean  — visual dash state (e.g. table "select all")
 *   label, hint: ReactNode
 */
const Checkbox = React.forwardRef(function Checkbox(
  { checked, onChange, indeterminate = false, label, hint, disabled, id, className = '', ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id || autoId;
  const innerRef = useRef(null);

  useEffect(() => {
    const node = innerRef.current;
    if (node) node.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  const control = (
    <span className="ps-checkbox">
      <input
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={inputId}
        type="checkbox"
        className="ps-checkbox__input"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        {...rest}
      />
      <span className="ps-checkbox__box" aria-hidden="true">
        {indeterminate && !checked ? <Icon.MoreHorizontal size={12} /> : <Icon.Check size={12} />}
      </span>
    </span>
  );

  if (!label && !hint) return control;

  return (
    <label className={`ps-checkbox-field ${disabled ? 'is-disabled' : ''} ${className}`.trim()} htmlFor={inputId}>
      {control}
      <span className="ps-checkbox-field__text">
        {label && <span className="ps-checkbox-field__label">{label}</span>}
        {hint && <span className="ps-checkbox-field__hint">{hint}</span>}
      </span>
    </label>
  );
});

export default Checkbox;
